import type { LocalizedArticle } from '@/types/article';

export const completeGuide: LocalizedArticle = {
  slug: 'allchinabuy-spreadsheet-complete-guide',
  tag: 'Complete Guide',
  date: '2026-08-25',
  readTime: 14,
  image:
    'https://readdy.ai/api/search-image?query=A%20clean%20modern%20desk%20setup%20with%20a%20large%20monitor%20showing%20an%20organized%20product%20spreadsheet%20next%20to%20a%20pair%20of%20white%20sneakers%20a%20folded%20hoodie%20and%20a%20leather%20bag%20arranged%20on%20a%20warm%20oat%20colored%20linen%20surface%20with%20soft%20diffused%20daylight%20editorial%20ecommerce%20research%20photography%20in%20calm%20neutral%20tones&width=1200&height=600&seq=rich-guide-hero-01&orientation=landscape',
  related: [
    { slug: 'is-allchinabuy-legit', label: 'Is AllChinaBuy Legit?' },
    { slug: 'allchinabuy-shipping-cost-guide', label: 'Shipping Cost Guide' },
    { slug: 'allchinabuy-review', label: 'AllChinaBuy Review' },
    { slug: 'allchinabuy-qc-guide', label: 'QC Photos Guide' },
    { slug: 'allchinabuy-coupon-guide', label: 'Coupon Guide' },
  ],
  content: {
    en: {
      metaTitle: 'AllChinaBuy Spreadsheet 2026: The Complete Buyer Guide',
      metaDescription:
        'The complete AllChinaBuy Spreadsheet guide for 2026 — how it works, how to read seller data, QC photos, shipping costs, real examples, pros and cons, and FAQs.',
      title: 'AllChinaBuy Spreadsheet: The Complete 2026 Buyer Guide',
      intro:
        "If you have spent any time researching how to buy from Chinese warehouses, you have probably run into the phrase \u201cAllChinaBuy Spreadsheet\u201d a dozen times \u2014 usually with almost no explanation of what it actually is or how to use it. This guide fixes that. Below you will find a genuinely complete walkthrough: what the spreadsheet is, how the columns work, how to vet sellers, how to read QC photos, what shipping really costs, and the small habits that separate a smooth first order from an expensive mistake. No hype, no affiliation claims \u2014 just the practical playbook we wish we had when we started.",
      blocks: [
        { type: 'h2', id: 's1', text: 'What the AllChinaBuy Spreadsheet Actually Is' },
        {
          type: 'p',
          text: 'At its core, the AllChinaBuy Spreadsheet is a curated index. Instead of scrolling endlessly through a marketplace and guessing which listing is trustworthy, the spreadsheet gathers vetted products into rows, each linking directly to a specific item and seller. Think of it less like a store and more like a well-organized address book for products \u2014 someone has already done the tedious work of finding the good listings so you do not have to.',
        },
        {
          type: 'p',
          text: 'The reason this format took off is simple: cross-border shopping is full of duplicate listings, mislabeled products, and sellers with wildly inconsistent quality. A spreadsheet flattens all of that into a scannable table where you can compare price, category, and seller reputation in seconds. Once you understand how to read it, the entire buying process becomes far less intimidating.',
        },
        {
          type: 'callout',
          title: 'Quick definition',
          text: 'A shopping-agent spreadsheet is a shared list of product links, seller names, and notes. It is a reference tool \u2014 you still buy through the platform itself, not through the spreadsheet.',
        },
        { type: 'h2', id: 's2', text: 'How to Read the Columns (Without Getting Overwhelmed)' },
        {
          type: 'p',
          text: 'The first time you open a large spreadsheet it can feel like a wall of text. The trick is to ignore most of it and focus on four columns that carry almost all of the decision-making weight.',
        },
        {
          type: 'table',
          headers: ['Column', 'What it tells you', 'How to use it'],
          rows: [
            ['Product name', 'The item and often the style or colorway', 'Scan quickly to shortlist; ignore vague names'],
            ['Category', 'Groups similar items (Shoes, Bags, etc.)', 'Filter to one category to avoid decision fatigue'],
            ['Seller', 'Which shop the link points to', 'Cross-check the seller before you ever add to cart'],
            ['Price range', 'Rough cost before shipping', 'Set expectations; suspiciously low prices are a flag'],
            ['Notes / QC', 'Buyer comments on quality or fit', 'Read these first \u2014 they save the most money'],
          ],
        },
        {
          type: 'p',
          text: 'Everything else \u2014 timestamps, internal IDs, occasional emojis \u2014 is noise for a first-time buyer. Master those five signals and you can evaluate a listing in under ten seconds.',
        },
        { type: 'h2', id: 's3', text: 'A Realistic First-Order Walkthrough' },
        {
          type: 'p',
          text: 'Let us walk through a concrete example. Say you want a pair of everyday sneakers. Here is the exact sequence an experienced buyer follows, step by step.',
        },
        {
          type: 'ol',
          items: [
            'Filter the spreadsheet to the Shoes category and skim for your style.',
            'Open two or three candidate links in new tabs \u2014 never commit to the first one.',
            'Check each seller\u2019s recent reviews, focusing on the last two months.',
            'Compare the size chart against a shoe you already own that fits well.',
            'Add the item to your cart and place the order through the platform.',
            'Wait for warehouse arrival, then request and study the QC photos.',
            'Approve, then consolidate with any other items before shipping.',
          ],
        },
        {
          type: 'p',
          text: 'Notice how much of this happens before you spend a cent, and how much happens after the item reaches the warehouse but before it ships to you. Those two checkpoints \u2014 seller vetting and QC \u2014 are where nearly every good outcome is won or lost.',
        },
        { type: 'h2', id: 's4', text: 'How to Vet a Seller in Under a Minute' },
        {
          type: 'p',
          text: 'A single star rating means very little on its own. What you actually want is a pattern of consistency. Two sellers can both show \u201c4.8 stars,\u201d yet one has 900 reviews spread evenly across a year while the other has 12 reviews from last week. The first is a far safer bet.',
        },
        {
          type: 'h3',
          text: 'Green flags',
        },
        {
          type: 'list',
          items: [
            'Steady positive reviews over many months, not a sudden burst',
            'A focused product range rather than \u201ceverything for everyone\u201d',
            'Fast, clear answers when you message a sizing question',
            'Recent QC photos in reviews that match the listing images',
          ],
        },
        {
          type: 'h3',
          text: 'Red flags',
        },
        {
          type: 'list',
          items: [
            'No review history or a brand-new account',
            'A cluster of recent complaints about quality or wrong items',
            'Prices dramatically lower than every comparable listing',
            'Slow, evasive, or copy-paste replies to direct questions',
          ],
        },
        { type: 'h2', id: 's5', text: 'Reading QC Photos Like a Pro' },
        {
          type: 'p',
          text: 'QC \u2014 quality control \u2014 photos are taken at the warehouse before your item ships internationally. This is your one clean chance to catch a defect while returning the item is still cheap and easy. Skipping this step is the single most common beginner mistake, and it is also the most expensive one.',
        },
        {
          type: 'p',
          text: 'When the photos arrive, do not just glance and approve. Zoom in. For footwear, check the stitching around the toe box, the sole bonding, and whether both shoes match. For clothing, look for even dye, clean seams, and correct measurements against the size chart. For bags, test the story the photos tell about hardware \u2014 zippers, clasps, and structure.',
        },
        {
          type: 'proscons',
          pros: [
            'Catch defects before paying for international shipping',
            'Confirm size and color match the listing',
            'Build confidence that the seller is reliable',
            'Create a photo record in case you need a refund',
          ],
          cons: [
            'Adds a short wait while photos are prepared',
            'Requires you to actually inspect, not rubber-stamp',
            'Some flaws are hard to judge from photos alone',
          ],
        },
        { type: 'h2', id: 's6', text: 'What Shipping Really Costs' },
        {
          type: 'p',
          text: 'Shipping is usually the part that surprises new buyers, because it is charged separately after your items reach the warehouse. The final price depends on weight, dimensions, destination, and the line you choose. As a rough orientation, here is how the trade-offs tend to look.',
        },
        {
          type: 'table',
          headers: ['Method', 'Typical speed', 'Cost level', 'Best for'],
          rows: [
            ['Express', '7\u201314 days', 'Higher', 'Time-sensitive or high-value orders'],
            ['Standard', '15\u201325 days', 'Medium', 'Most everyday purchases'],
            ['Economy', '25\u201340 days', 'Lower', 'Heavy items where cost matters most'],
          ],
        },
        {
          type: 'p',
          text: 'The biggest lever you control is consolidation \u2014 combining several items into one package so you pay one shipping fee instead of many. For a deeper, country-by-country breakdown, see our dedicated shipping cost guide linked at the end of this article.',
        },
        { type: 'h2', id: 's7', text: 'Pros and Cons of Buying This Way' },
        {
          type: 'p',
          text: 'No method is perfect. Being honest about the trade-offs will make you a calmer, smarter buyer.',
        },
        {
          type: 'proscons',
          pros: [
            'Access to products that are hard to find locally',
            'Strong price advantage on many categories',
            'QC photos give real control over quality',
            'Consolidation keeps shipping efficient',
          ],
          cons: [
            'Longer delivery times than domestic shopping',
            'A learning curve on your first few orders',
            'Quality varies, so seller vetting is essential',
            'Shipping cost can surprise the unprepared',
          ],
        },
        { type: 'h2', id: 's8', text: 'Final Tips Before Your First Order' },
        {
          type: 'p',
          text: 'Start small. Your first order is a lesson, not a shopping spree \u2014 buy one low-risk item, run through the full process, and learn where you hesitate. Keep screenshots of your orders and conversations. Always use the platform\u2019s own payment and messaging so you stay protected. Do these few things and the rest becomes muscle memory surprisingly fast.',
        },
        {
          type: 'quote',
          text: 'The buyers who have the best experiences are not the ones who spend the most \u2014 they are the ones who slow down at two moments: choosing the seller, and reading the QC photos.',
        },
      ],
      faqs: [
        {
          q: 'Do I need to pay to access the spreadsheet?',
          a: 'The spreadsheet itself is a reference index. You only pay when you buy the items it links to, and payment happens through the platform, not the spreadsheet.',
        },
        {
          q: 'Is the AllChinaBuy Spreadsheet official?',
          a: 'This site is an independent resource and is not officially affiliated with AllChinaBuy. We aggregate and explain publicly available information to help you shop more confidently.',
        },
        {
          q: 'How long does a typical order take to arrive?',
          a: 'It varies by shipping method and destination, but most orders land somewhere between two and five weeks once you factor in processing, QC, and international transit.',
        },
        {
          q: 'What is the single most important step for beginners?',
          a: 'Reading the QC photos carefully before approving shipment. It is the cheapest, easiest moment to catch a problem, and skipping it causes the most regret.',
        },
      ],
    },
    fr: {
      metaTitle: 'AllChinaBuy Spreadsheet 2026 : le guide complet de l\u2019acheteur',
      metaDescription:
        'Le guide complet du AllChinaBuy Spreadsheet pour 2026 \u2014 fonctionnement, lecture des donn\u00e9es vendeur, photos QC, co\u00fbts d\u2019exp\u00e9dition, exemples r\u00e9els, avantages, inconv\u00e9nients et FAQ.',
      title: 'AllChinaBuy Spreadsheet : le guide complet de l\u2019acheteur 2026',
      intro:
        "Si vous avez cherch\u00e9 comment acheter depuis les entrep\u00f4ts chinois, vous avez sans doute crois\u00e9 l\u2019expression \u00ab AllChinaBuy Spreadsheet \u00bb des dizaines de fois \u2014 presque toujours sans explication concr\u00e8te. Ce guide corrige cela. Vous trouverez ci-dessous une v\u00e9ritable visite compl\u00e8te : ce qu\u2019est le spreadsheet, comment fonctionnent les colonnes, comment \u00e9valuer les vendeurs, comment lire les photos QC, ce que co\u00fbte vraiment l\u2019exp\u00e9dition, et les petites habitudes qui distinguent une premi\u00e8re commande r\u00e9ussie d\u2019une erreur co\u00fbteuse. Sans battage, sans revendication d\u2019affiliation \u2014 juste la m\u00e9thode pratique que nous aurions aim\u00e9 conna\u00eetre au d\u00e9but.",
      blocks: [
        { type: 'h2', id: 's1', text: 'Ce qu\u2019est vraiment le AllChinaBuy Spreadsheet' },
        {
          type: 'p',
          text: 'Au fond, le AllChinaBuy Spreadsheet est un index organis\u00e9. Plut\u00f4t que de faire d\u00e9filer sans fin une marketplace en devinant quelle annonce est fiable, le spreadsheet rassemble des produits v\u00e9rifi\u00e9s en lignes, chacune renvoyant directement vers un article et un vendeur pr\u00e9cis. Voyez-le moins comme une boutique que comme un carnet d\u2019adresses de produits bien organis\u00e9 \u2014 quelqu\u2019un a d\u00e9j\u00e0 fait le travail fastidieux de trouver les bonnes annonces \u00e0 votre place.',
        },
        {
          type: 'p',
          text: 'Si ce format a pris autant d\u2019ampleur, la raison est simple : le shopping transfrontalier regorge d\u2019annonces dupliqu\u00e9es, de produits mal \u00e9tiquet\u00e9s et de vendeurs \u00e0 la qualit\u00e9 tr\u00e8s in\u00e9gale. Un spreadsheet aplatit tout cela dans un tableau lisible o\u00f9 vous comparez prix, cat\u00e9gorie et r\u00e9putation en quelques secondes. Une fois que vous savez le lire, tout le processus d\u2019achat devient bien moins intimidant.',
        },
        {
          type: 'callout',
          title: 'D\u00e9finition rapide',
          text: 'Un spreadsheet d\u2019agent d\u2019achat est une liste partag\u00e9e de liens produits, de noms de vendeurs et de notes. C\u2019est un outil de r\u00e9f\u00e9rence \u2014 vous achetez toujours via la plateforme, pas via le spreadsheet.',
        },
        { type: 'h2', id: 's2', text: 'Lire les colonnes sans se noyer' },
        {
          type: 'p',
          text: 'La premi\u00e8re fois que vous ouvrez un grand spreadsheet, cela ressemble \u00e0 un mur de texte. L\u2019astuce est d\u2019en ignorer la majeure partie et de vous concentrer sur quatre colonnes qui portent presque toute la d\u00e9cision.',
        },
        {
          type: 'table',
          headers: ['Colonne', 'Ce qu\u2019elle indique', 'Comment l\u2019utiliser'],
          rows: [
            ['Nom du produit', 'L\u2019article et souvent le mod\u00e8le ou coloris', 'Survolez pour pr\u00e9s\u00e9lectionner ; ignorez les noms vagues'],
            ['Cat\u00e9gorie', 'Regroupe les articles similaires (Chaussures, Sacs\u2026)', 'Filtrez sur une cat\u00e9gorie pour \u00e9viter la fatigue de choix'],
            ['Vendeur', 'La boutique vers laquelle pointe le lien', 'V\u00e9rifiez le vendeur avant m\u00eame d\u2019ajouter au panier'],
            ['Fourchette de prix', 'Co\u00fbt approximatif hors exp\u00e9dition', 'Cadrez vos attentes ; un prix trop bas est un signal'],
            ['Notes / QC', 'Commentaires acheteurs sur qualit\u00e9 ou taille', 'Lisez-les en premier \u2014 ils font \u00e9conomiser le plus'],
          ],
        },
        {
          type: 'p',
          text: 'Tout le reste \u2014 horodatages, identifiants internes, emojis \u2014 est du bruit pour un premier achat. Ma\u00eetrisez ces cinq signaux et vous \u00e9valuez une annonce en moins de dix secondes.',
        },
        { type: 'h2', id: 's3', text: 'Une premi\u00e8re commande, \u00e9tape par \u00e9tape' },
        {
          type: 'p',
          text: 'Prenons un exemple concret. Disons que vous voulez une paire de sneakers du quotidien. Voici la s\u00e9quence exacte que suit un acheteur exp\u00e9riment\u00e9.',
        },
        {
          type: 'ol',
          items: [
            'Filtrez le spreadsheet sur la cat\u00e9gorie Chaussures et rep\u00e9rez votre style.',
            'Ouvrez deux ou trois liens candidats dans de nouveaux onglets \u2014 ne foncez jamais sur le premier.',
            'V\u00e9rifiez les avis r\u00e9cents de chaque vendeur, surtout les deux derniers mois.',
            'Comparez le guide des tailles \u00e0 une chaussure que vous poss\u00e9dez d\u00e9j\u00e0 et qui vous va bien.',
            'Ajoutez l\u2019article au panier et passez commande via la plateforme.',
            'Attendez l\u2019arriv\u00e9e \u00e0 l\u2019entrep\u00f4t, puis demandez et \u00e9tudiez les photos QC.',
            'Approuvez, puis regroupez avec vos autres articles avant l\u2019exp\u00e9dition.',
          ],
        },
        {
          type: 'p',
          text: 'Remarquez tout ce qui se passe avant de d\u00e9penser un centime, et tout ce qui se passe apr\u00e8s l\u2019arriv\u00e9e \u00e0 l\u2019entrep\u00f4t mais avant l\u2019envoi. Ces deux points de contr\u00f4le \u2014 le choix du vendeur et le QC \u2014 d\u00e9terminent presque toujours le r\u00e9sultat.',
        },
        { type: 'h2', id: 's4', text: '\u00c9valuer un vendeur en moins d\u2019une minute' },
        {
          type: 'p',
          text: 'Une simple note en \u00e9toiles ne dit presque rien \u00e0 elle seule. Ce que vous cherchez, c\u2019est un motif de constance. Deux vendeurs peuvent afficher \u00ab 4,8 \u00e9toiles \u00bb, mais l\u2019un a 900 avis r\u00e9partis sur un an tandis que l\u2019autre a 12 avis de la semaine derni\u00e8re. Le premier est bien plus s\u00fbr.',
        },
        { type: 'h3', text: 'Signaux positifs' },
        {
          type: 'list',
          items: [
            'Des avis positifs r\u00e9guliers sur plusieurs mois, pas un pic soudain',
            'Une gamme cibl\u00e9e plut\u00f4t que \u00ab tout pour tout le monde \u00bb',
            'Des r\u00e9ponses rapides et claires \u00e0 une question de taille',
            'Des photos QC r\u00e9centes dans les avis, conformes aux images',
          ],
        },
        { type: 'h3', text: 'Signaux d\u2019alerte' },
        {
          type: 'list',
          items: [
            'Aucun historique d\u2019avis ou un compte tout neuf',
            'Une grappe de plaintes r\u00e9centes sur la qualit\u00e9 ou les erreurs',
            'Des prix bien plus bas que toute annonce comparable',
            'Des r\u00e9ponses lentes, \u00e9vasives ou copi\u00e9es-coll\u00e9es',
          ],
        },
        { type: 'h2', id: 's5', text: 'Lire les photos QC comme un pro' },
        {
          type: 'p',
          text: 'Les photos QC \u2014 contr\u00f4le qualit\u00e9 \u2014 sont prises \u00e0 l\u2019entrep\u00f4t avant l\u2019envoi international. C\u2019est votre unique occasion propre de rep\u00e9rer un d\u00e9faut tant que le retour reste simple et bon march\u00e9. Sauter cette \u00e9tape est l\u2019erreur de d\u00e9butant la plus courante, et la plus co\u00fbteuse.',
        },
        {
          type: 'p',
          text: 'Quand les photos arrivent, ne jetez pas un simple coup d\u2019\u0153il pour approuver. Zoomez. Pour les chaussures, v\u00e9rifiez les coutures du bout, le collage de la semelle et si les deux chaussures correspondent. Pour les v\u00eatements, cherchez une teinture uniforme, des coutures nettes et des mesures correctes. Pour les sacs, examinez la quincaillerie : fermetures, fermoirs et structure.',
        },
        {
          type: 'proscons',
          pros: [
            'Rep\u00e9rer les d\u00e9fauts avant de payer l\u2019exp\u00e9dition internationale',
            'Confirmer que taille et couleur correspondent \u00e0 l\u2019annonce',
            'Gagner en confiance sur la fiabilit\u00e9 du vendeur',
            'Constituer une preuve photo en cas de remboursement',
          ],
          cons: [
            'Ajoute une courte attente le temps des photos',
            'Exige une vraie inspection, pas une validation machinale',
            'Certains d\u00e9fauts sont durs \u00e0 juger sur photo',
          ],
        },
        { type: 'h2', id: 's6', text: 'Ce que co\u00fbte vraiment l\u2019exp\u00e9dition' },
        {
          type: 'p',
          text: 'L\u2019exp\u00e9dition est souvent ce qui surprend les nouveaux acheteurs, car elle est factur\u00e9e s\u00e9par\u00e9ment apr\u00e8s l\u2019arriv\u00e9e \u00e0 l\u2019entrep\u00f4t. Le prix final d\u00e9pend du poids, des dimensions, de la destination et de la ligne choisie. \u00c0 titre indicatif, voici les compromis habituels.',
        },
        {
          type: 'table',
          headers: ['M\u00e9thode', 'D\u00e9lai typique', 'Niveau de co\u00fbt', 'Id\u00e9al pour'],
          rows: [
            ['Express', '7\u201314 jours', '\u00c9lev\u00e9', 'Commandes urgentes ou de grande valeur'],
            ['Standard', '15\u201325 jours', 'Moyen', 'La plupart des achats courants'],
            ['\u00c9conomique', '25\u201340 jours', 'Bas', 'Articles lourds o\u00f9 le co\u00fbt prime'],
          ],
        },
        {
          type: 'p',
          text: 'Le principal levier que vous ma\u00eetrisez est le regroupement \u2014 combiner plusieurs articles en un seul colis pour ne payer qu\u2019un envoi. Pour un d\u00e9tail pays par pays, voyez notre guide d\u00e9di\u00e9 aux co\u00fbts d\u2019exp\u00e9dition en fin d\u2019article.',
        },
        { type: 'h2', id: 's7', text: 'Avantages et inconv\u00e9nients de cette m\u00e9thode' },
        {
          type: 'p',
          text: 'Aucune m\u00e9thode n\u2019est parfaite. \u00catre honn\u00eate sur les compromis fera de vous un acheteur plus calme et plus avis\u00e9.',
        },
        {
          type: 'proscons',
          pros: [
            'Acc\u00e8s \u00e0 des produits difficiles \u00e0 trouver localement',
            'Fort avantage de prix sur de nombreuses cat\u00e9gories',
            'Les photos QC donnent un vrai contr\u00f4le sur la qualit\u00e9',
            'Le regroupement garde l\u2019exp\u00e9dition efficace',
          ],
          cons: [
            'D\u00e9lais plus longs que le shopping local',
            'Une courbe d\u2019apprentissage sur les premi\u00e8res commandes',
            'La qualit\u00e9 varie, d\u2019o\u00f9 l\u2019importance du choix du vendeur',
            'Le co\u00fbt d\u2019exp\u00e9dition peut surprendre les non-pr\u00e9par\u00e9s',
          ],
        },
        { type: 'h2', id: 's8', text: 'Derniers conseils avant votre premi\u00e8re commande' },
        {
          type: 'p',
          text: 'Commencez petit. Votre premi\u00e8re commande est une le\u00e7on, pas une razzia \u2014 achetez un article \u00e0 faible risque, parcourez tout le processus et rep\u00e9rez o\u00f9 vous h\u00e9sitez. Gardez des captures de vos commandes et \u00e9changes. Utilisez toujours le paiement et la messagerie de la plateforme pour rester prot\u00e9g\u00e9. Faites ces quelques choses et le reste devient vite un automatisme.',
        },
        {
          type: 'quote',
          text: 'Les acheteurs qui vivent les meilleures exp\u00e9riences ne sont pas ceux qui d\u00e9pensent le plus \u2014 ce sont ceux qui ralentissent \u00e0 deux moments : le choix du vendeur et la lecture des photos QC.',
        },
      ],
      faqs: [
        {
          q: 'Dois-je payer pour acc\u00e9der au spreadsheet ?',
          a: 'Le spreadsheet lui-m\u00eame est un index de r\u00e9f\u00e9rence. Vous ne payez que lorsque vous achetez les articles qu\u2019il r\u00e9f\u00e9rence, et le paiement se fait via la plateforme, pas le spreadsheet.',
        },
        {
          q: 'Le AllChinaBuy Spreadsheet est-il officiel ?',
          a: 'Ce site est une ressource ind\u00e9pendante, non affili\u00e9e officiellement \u00e0 AllChinaBuy. Nous rassemblons et expliquons des informations publiques pour vous aider \u00e0 acheter plus sereinement.',
        },
        {
          q: 'Combien de temps met une commande \u00e0 arriver ?',
          a: 'Cela d\u00e9pend de la m\u00e9thode et de la destination, mais la plupart des commandes arrivent entre deux et cinq semaines une fois le traitement, le QC et le transport pris en compte.',
        },
        {
          q: 'Quelle est l\u2019\u00e9tape la plus importante pour d\u00e9buter ?',
          a: 'Lire attentivement les photos QC avant d\u2019approuver l\u2019envoi. C\u2019est le moment le moins cher et le plus simple pour rep\u00e9rer un probl\u00e8me.',
        },
      ],
    },
    da: {
      metaTitle: 'AllChinaBuy Spreadsheet 2026: Den komplette k\u00f8berguide',
      metaDescription:
        'Den komplette AllChinaBuy Spreadsheet-guide til 2026 \u2014 s\u00e5dan virker den, l\u00e6sning af s\u00e6lgerdata, QC-billeder, fragtomkostninger, eksempler, fordele, ulemper og FAQ.',
      title: 'AllChinaBuy Spreadsheet: Den komplette k\u00f8berguide 2026',
      intro:
        "Hvis du har brugt tid p\u00e5 at unders\u00f8ge, hvordan man k\u00f8ber fra kinesiske lagre, er du sikkert st\u00f8dt p\u00e5 udtrykket \u201cAllChinaBuy Spreadsheet\u201d et utal af gange \u2014 n\u00e6sten altid uden nogen forklaring p\u00e5, hvad det egentlig er. Denne guide retter op p\u00e5 det. Nedenfor finder du en virkelig komplet gennemgang: hvad regnearket er, hvordan kolonnerne fungerer, hvordan du vurderer s\u00e6lgere, hvordan du l\u00e6ser QC-billeder, hvad fragt reelt koster, og de sm\u00e5 vaner, der adskiller en god f\u00f8rste ordre fra en dyr fejl. Ingen hype, ingen p\u00e5stande om tilknytning \u2014 bare den praktiske drejebog, vi selv \u00f8nskede, vi havde haft fra start.",
      blocks: [
        { type: 'h2', id: 's1', text: 'Hvad AllChinaBuy Spreadsheet faktisk er' },
        {
          type: 'p',
          text: 'I bund og grund er AllChinaBuy Spreadsheet et kurateret indeks. I stedet for at scrolle endel\u00f8st gennem en markedsplads og g\u00e6tte, hvilken annonce der er til at stole p\u00e5, samler regnearket verificerede produkter i r\u00e6kker, hver med et direkte link til en bestemt vare og s\u00e6lger. T\u00e6nk p\u00e5 det mindre som en butik og mere som en velorganiseret adressebog over produkter \u2014 nogen har allerede gjort det kedelige arbejde med at finde de gode annoncer for dig.',
        },
        {
          type: 'p',
          text: 'Grunden til, at formatet blev popul\u00e6rt, er enkel: gr\u00e6nsehandel er fuld af duplikerede annoncer, forkert m\u00e6rkede produkter og s\u00e6lgere med meget svingende kvalitet. Et regneark fladg\u00f8r alt det til en overskuelig tabel, hvor du kan sammenligne pris, kategori og omd\u00f8mme p\u00e5 f\u00e5 sekunder. N\u00e5r du forst\u00e5r at l\u00e6se det, bliver hele k\u00f8bsprocessen langt mindre skr\u00e6mmende.',
        },
        {
          type: 'callout',
          title: 'Hurtig definition',
          text: 'Et shopping-agent-regneark er en delt liste over produktlinks, s\u00e6lgernavne og noter. Det er et opslagsv\u00e6rkt\u00f8j \u2014 du k\u00f8ber stadig via selve platformen, ikke via regnearket.',
        },
        { type: 'h2', id: 's2', text: 'L\u00e6s kolonnerne uden at drukne' },
        {
          type: 'p',
          text: 'F\u00f8rste gang du \u00e5bner et stort regneark, kan det ligne en mur af tekst. Tricket er at ignorere det meste og fokusere p\u00e5 fire kolonner, der b\u00e6rer n\u00e6sten hele beslutningen.',
        },
        {
          type: 'table',
          headers: ['Kolonne', 'Hvad den fort\u00e6ller', 'S\u00e5dan bruger du den'],
          rows: [
            ['Produktnavn', 'Varen og ofte stil eller farve', 'Skim hurtigt for at shortliste; ignorer vage navne'],
            ['Kategori', 'Grupperer lignende varer (Sko, Tasker\u2026)', 'Filtr\u00e9r til \u00e9n kategori for at undg\u00e5 valgtr\u00e6thed'],
            ['S\u00e6lger', 'Hvilken butik linket peger p\u00e5', 'Tjek s\u00e6lgeren, f\u00f8r du overhovedet l\u00e6gger i kurv'],
            ['Prisinterval', 'Cirkapris f\u00f8r fragt', 'S\u00e6t forventninger; mist\u00e6nkeligt lave priser er et flag'],
            ['Noter / QC', 'K\u00f8berkommentarer om kvalitet eller pasform', 'L\u00e6s dem f\u00f8rst \u2014 de sparer flest penge'],
          ],
        },
        {
          type: 'p',
          text: 'Alt andet \u2014 tidsstempler, interne id\u2019er, emojis \u2014 er st\u00f8j for en f\u00f8rstegangsk\u00f8ber. Mestr\u00e9r de fem signaler, og du kan vurdere en annonce p\u00e5 under ti sekunder.',
        },
        { type: 'h2', id: 's3', text: 'En realistisk f\u00f8rste ordre, trin for trin' },
        {
          type: 'p',
          text: 'Lad os tage et konkret eksempel. Sig, du vil have et par hverdagssneakers. Her er den pr\u00e6cise r\u00e6kkef\u00f8lge, en erfaren k\u00f8ber f\u00f8lger.',
        },
        {
          type: 'ol',
          items: [
            'Filtr\u00e9r regnearket til kategorien Sko og find din stil.',
            '\u00c5bn to-tre kandidatlinks i nye faner \u2014 vælg aldrig det f\u00f8rste.',
            'Tjek hver s\u00e6lgers seneste anmeldelser, is\u00e6r de sidste to m\u00e5neder.',
            'Sammenlign st\u00f8rrelsesguiden med en sko, du allerede har, og som passer.',
            'L\u00e6g varen i kurven og bestil via platformen.',
            'Vent p\u00e5 lagerankomst, og bed derefter om og studer QC-billederne.',
            'Godkend, og konsolid\u00e9r med andre varer f\u00f8r forsendelse.',
          ],
        },
        {
          type: 'p',
          text: 'L\u00e6g m\u00e6rke til, hvor meget der sker, f\u00f8r du bruger en krone, og hvor meget der sker, efter varen n\u00e5r lageret, men f\u00f8r den sendes. De to kontrolpunkter \u2014 s\u00e6lgervurdering og QC \u2014 afg\u00f8r n\u00e6sten altid resultatet.',
        },
        { type: 'h2', id: 's4', text: 'Vurder en s\u00e6lger p\u00e5 under et minut' },
        {
          type: 'p',
          text: 'En enkelt stjernevurdering siger meget lidt alene. Det, du reelt vil have, er et m\u00f8nster af konsistens. To s\u00e6lgere kan begge vise \u201c4,8 stjerner\u201d, men den ene har 900 anmeldelser j\u00e6vnt fordelt over et \u00e5r, mens den anden har 12 fra sidste uge. Den f\u00f8rste er langt mere sikker.',
        },
        { type: 'h3', text: 'Gr\u00f8nne flag' },
        {
          type: 'list',
          items: [
            'Stabile positive anmeldelser over mange m\u00e5neder, ikke et pludseligt spring',
            'Et fokuseret sortiment frem for \u201calt til alle\u201d',
            'Hurtige, klare svar n\u00e5r du sp\u00f8rger om st\u00f8rrelse',
            'Nye QC-billeder i anmeldelser, der matcher annoncen',
          ],
        },
        { type: 'h3', text: 'R\u00f8de flag' },
        {
          type: 'list',
          items: [
            'Ingen anmeldelseshistorik eller en helt ny konto',
            'En klynge af nylige klager om kvalitet eller forkerte varer',
            'Priser markant lavere end enhver sammenlignelig annonce',
            'Langsomme, undvigende eller kopierede svar',
          ],
        },
        { type: 'h2', id: 's5', text: 'L\u00e6s QC-billeder som en professionel' },
        {
          type: 'p',
          text: 'QC-billeder \u2014 kvalitetskontrol \u2014 tages p\u00e5 lageret, f\u00f8r din vare sendes internationalt. Det er din ene rene chance for at fange en fejl, mens returnering stadig er billig og let. At springe dette over er den mest almindelige begynderfejl og ogs\u00e5 den dyreste.',
        },
        {
          type: 'p',
          text: 'N\u00e5r billederne kommer, s\u00e5 nej til bare at kigge og godkende. Zoom ind. For fodt\u00f8j: tjek syningerne ved t\u00e5en, s\u00e5lens binding og om begge sko matcher. For t\u00f8j: kig efter ensartet farve, rene s\u00f8mme og korrekte m\u00e5l. For tasker: unders\u00f8g besl\u00e5g \u2014 lynl\u00e5se, sp\u00e6nder og struktur.',
        },
        {
          type: 'proscons',
          pros: [
            'Fang fejl, f\u00f8r du betaler international fragt',
            'Bekr\u00e6ft at st\u00f8rrelse og farve matcher annoncen',
            'F\u00e5 tillid til at s\u00e6lgeren er p\u00e5lidelig',
            'Skab et fotobevis, hvis du beh\u00f8ver refusion',
          ],
          cons: [
            'Tilf\u00f8jer en kort ventetid, mens billeder laves',
            'Kr\u00e6ver at du faktisk inspicerer, ikke bare godkender',
            'Nogle fejl er sv\u00e6re at bed\u00f8mme p\u00e5 billeder alene',
          ],
        },
        { type: 'h2', id: 's6', text: 'Hvad fragt reelt koster' },
        {
          type: 'p',
          text: 'Fragt er ofte det, der overrasker nye k\u00f8bere, fordi den opkr\u00e6ves separat, efter dine varer n\u00e5r lageret. Slutprisen afh\u00e6nger af v\u00e6gt, dimensioner, destination og den valgte linje. Som grov orientering ser afvejningerne s\u00e5dan ud.',
        },
        {
          type: 'table',
          headers: ['Metode', 'Typisk hastighed', 'Omkostningsniveau', 'Bedst til'],
          rows: [
            ['Express', '7\u201314 dage', 'H\u00f8jere', 'Tidsf\u00f8lsomme eller v\u00e6rdifulde ordrer'],
            ['Standard', '15\u201325 dage', 'Middel', 'De fleste hverdagsk\u00f8b'],
            ['\u00d8konomi', '25\u201340 dage', 'Lavere', 'Tunge varer hvor pris betyder mest'],
          ],
        },
        {
          type: 'p',
          text: 'Den st\u00f8rste h\u00e5ndtag, du styrer, er konsolidering \u2014 at samle flere varer i \u00e9n pakke, s\u00e5 du betaler \u00e9n fragt i stedet for mange. For en dybere gennemgang land for land, se vores dedikerede fragtguide i slutningen af artiklen.',
        },
        { type: 'h2', id: 's7', text: 'Fordele og ulemper ved denne m\u00e5de' },
        {
          type: 'p',
          text: 'Ingen metode er perfekt. At v\u00e6re \u00e6rlig om afvejningerne g\u00f8r dig til en roligere og klogere k\u00f8ber.',
        },
        {
          type: 'proscons',
          pros: [
            'Adgang til produkter, der er sv\u00e6re at finde lokalt',
            'St\u00e6rk prisfordel i mange kategorier',
            'QC-billeder giver reel kontrol over kvaliteten',
            'Konsolidering holder fragten effektiv',
          ],
          cons: [
            'L\u00e6ngere leveringstider end lokal shopping',
            'En l\u00e6ringskurve p\u00e5 de f\u00f8rste ordrer',
            'Kvaliteten varierer, s\u00e5 s\u00e6lgervurdering er afg\u00f8rende',
            'Fragtomkostninger kan overraske den uforberedte',
          ],
        },
        { type: 'h2', id: 's8', text: 'Sidste tips f\u00f8r din f\u00f8rste ordre' },
        {
          type: 'p',
          text: 'Start i det sm\u00e5. Din f\u00f8rste ordre er en l\u00e6ktion, ikke et shoppingtogt \u2014 k\u00f8b \u00e9n lavrisikovare, gennemg\u00e5 hele processen og l\u00e6r, hvor du t\u00f8ver. Gem screenshots af ordrer og samtaler. Brug altid platformens egen betaling og beskeder, s\u00e5 du er beskyttet. G\u00f8r disse f\u00e5 ting, og resten bliver hurtigt rygmarvsviden.',
        },
        {
          type: 'quote',
          text: 'De k\u00f8bere, der f\u00e5r de bedste oplevelser, er ikke dem, der bruger mest \u2014 det er dem, der saktner farten to steder: valg af s\u00e6lger og l\u00e6sning af QC-billeder.',
        },
      ],
      faqs: [
        {
          q: 'Skal jeg betale for at f\u00e5 adgang til regnearket?',
          a: 'Selve regnearket er et opslagsindeks. Du betaler kun, n\u00e5r du k\u00f8ber de varer, det linker til, og betalingen sker via platformen, ikke regnearket.',
        },
        {
          q: 'Er AllChinaBuy Spreadsheet officielt?',
          a: 'Dette site er en uafh\u00e6ngig ressource og er ikke officielt tilknyttet AllChinaBuy. Vi samler og forklarer offentligt tilg\u00e6ngelig information, s\u00e5 du kan handle mere trygt.',
        },
        {
          q: 'Hvor lang tid tager en typisk ordre?',
          a: 'Det varierer med fragtmetode og destination, men de fleste ordrer ankommer mellem to og fem uger, n\u00e5r behandling, QC og international transport medregnes.',
        },
        {
          q: 'Hvad er det vigtigste trin for begyndere?',
          a: 'At l\u00e6se QC-billederne grundigt, f\u00f8r du godkender forsendelse. Det er det billigste og letteste tidspunkt at fange et problem.',
        },
      ],
    },
    de: {
      metaTitle: 'AllChinaBuy Spreadsheet 2026: Der komplette K\u00e4uferleitfaden',
      metaDescription:
        'Der komplette AllChinaBuy-Spreadsheet-Leitfaden f\u00fcr 2026 \u2014 Funktionsweise, Lesen von Verk\u00e4uferdaten, QC-Fotos, Versandkosten, Beispiele, Vor- und Nachteile und FAQ.',
      title: 'AllChinaBuy Spreadsheet: Der komplette K\u00e4uferleitfaden 2026',
      intro:
        "Wenn Sie sich damit besch\u00e4ftigt haben, wie man aus chinesischen Lagern kauft, ist Ihnen der Begriff \u201eAllChinaBuy Spreadsheet\u201c wahrscheinlich Dutzende Male begegnet \u2014 fast immer ohne echte Erkl\u00e4rung. Dieser Leitfaden \u00e4ndert das. Unten finden Sie eine wirklich vollst\u00e4ndige Anleitung: was das Spreadsheet ist, wie die Spalten funktionieren, wie Sie Verk\u00e4ufer pr\u00fcfen, wie Sie QC-Fotos lesen, was Versand wirklich kostet und die kleinen Gewohnheiten, die eine reibungslose erste Bestellung von einem teuren Fehler unterscheiden. Kein Hype, keine Zugeh\u00f6rigkeitsbehauptungen \u2014 nur der praktische Leitfaden, den wir uns am Anfang selbst gew\u00fcnscht h\u00e4tten.",
      blocks: [
        { type: 'h2', id: 's1', text: 'Was das AllChinaBuy Spreadsheet wirklich ist' },
        {
          type: 'p',
          text: 'Im Kern ist das AllChinaBuy Spreadsheet ein kuratierter Index. Statt endlos durch einen Marktplatz zu scrollen und zu raten, welches Angebot vertrauensw\u00fcrdig ist, sammelt das Spreadsheet gepr\u00fcfte Produkte in Zeilen, die jeweils direkt zu einem bestimmten Artikel und Verk\u00e4ufer verlinken. Betrachten Sie es weniger als Laden und mehr als gut organisiertes Adressbuch f\u00fcr Produkte \u2014 jemand hat die m\u00fchsame Arbeit, die guten Angebote zu finden, bereits f\u00fcr Sie erledigt.',
        },
        {
          type: 'p',
          text: 'Warum sich dieses Format durchsetzte, ist einfach: grenz\u00fcberschreitendes Shopping steckt voller doppelter Angebote, falsch beschrifteter Produkte und Verk\u00e4ufer mit stark schwankender Qualit\u00e4t. Ein Spreadsheet verdichtet all das in eine \u00fcbersichtliche Tabelle, in der Sie Preis, Kategorie und Ruf in Sekunden vergleichen. Sobald Sie es lesen k\u00f6nnen, wird der gesamte Kaufprozess deutlich weniger einsch\u00fcchternd.',
        },
        {
          type: 'callout',
          title: 'Kurze Definition',
          text: 'Ein Shopping-Agent-Spreadsheet ist eine geteilte Liste aus Produktlinks, Verk\u00e4ufernamen und Notizen. Es ist ein Nachschlagewerk \u2014 Sie kaufen weiterhin \u00fcber die Plattform, nicht \u00fcber das Spreadsheet.',
        },
        { type: 'h2', id: 's2', text: 'Die Spalten lesen, ohne unterzugehen' },
        {
          type: 'p',
          text: 'Beim ersten \u00d6ffnen eines gro\u00dfen Spreadsheets wirkt es wie eine Textwand. Der Trick: Ignorieren Sie das meiste und konzentrieren Sie sich auf vier Spalten, die fast die gesamte Entscheidung tragen.',
        },
        {
          type: 'table',
          headers: ['Spalte', 'Was sie verr\u00e4t', 'So nutzen Sie sie'],
          rows: [
            ['Produktname', 'Der Artikel und oft Stil oder Farbe', 'Schnell \u00fcberfliegen; vage Namen ignorieren'],
            ['Kategorie', 'Gruppiert \u00e4hnliche Artikel (Schuhe, Taschen\u2026)', 'Auf eine Kategorie filtern gegen Entscheidungsm\u00fcdigkeit'],
            ['Verk\u00e4ufer', 'Auf welchen Shop der Link zeigt', 'Verk\u00e4ufer pr\u00fcfen, bevor Sie in den Warenkorb legen'],
            ['Preisspanne', 'Ungef\u00e4hrer Preis vor Versand', 'Erwartungen setzen; verd\u00e4chtig niedrige Preise sind ein Signal'],
            ['Notizen / QC', 'K\u00e4uferkommentare zu Qualit\u00e4t oder Passform', 'Zuerst lesen \u2014 sie sparen am meisten Geld'],
          ],
        },
        {
          type: 'p',
          text: 'Alles andere \u2014 Zeitstempel, interne IDs, Emojis \u2014 ist f\u00fcr Erstk\u00e4ufer Rauschen. Beherrschen Sie diese f\u00fcnf Signale, und Sie bewerten ein Angebot in unter zehn Sekunden.',
        },
        { type: 'h2', id: 's3', text: 'Eine realistische erste Bestellung, Schritt f\u00fcr Schritt' },
        {
          type: 'p',
          text: 'Nehmen wir ein konkretes Beispiel. Angenommen, Sie wollen ein Paar Alltagssneaker. Hier ist die genaue Abfolge, der ein erfahrener K\u00e4ufer folgt.',
        },
        {
          type: 'ol',
          items: [
            'Filtern Sie das Spreadsheet auf die Kategorie Schuhe und suchen Sie Ihren Stil.',
            '\u00d6ffnen Sie zwei bis drei Kandidatenlinks in neuen Tabs \u2014 nie den ersten nehmen.',
            'Pr\u00fcfen Sie die j\u00fcngsten Bewertungen jedes Verk\u00e4ufers, vor allem der letzten zwei Monate.',
            'Vergleichen Sie die Gr\u00f6\u00dfentabelle mit einem gut passenden Schuh, den Sie schon besitzen.',
            'Legen Sie den Artikel in den Warenkorb und bestellen Sie \u00fcber die Plattform.',
            'Warten Sie auf den Lagereingang, fordern und pr\u00fcfen Sie dann die QC-Fotos.',
            'Best\u00e4tigen Sie und konsolidieren Sie vor dem Versand mit weiteren Artikeln.',
          ],
        },
        {
          type: 'p',
          text: 'Beachten Sie, wie viel passiert, bevor Sie einen Cent ausgeben, und wie viel nach dem Lagereingang, aber vor dem Versand. Diese beiden Kontrollpunkte \u2014 Verk\u00e4uferpr\u00fcfung und QC \u2014 entscheiden fast immer \u00fcber das Ergebnis.',
        },
        { type: 'h2', id: 's4', text: 'Einen Verk\u00e4ufer in unter einer Minute pr\u00fcfen' },
        {
          type: 'p',
          text: 'Eine einzelne Sternebewertung sagt f\u00fcr sich genommen sehr wenig. Was Sie wirklich wollen, ist ein Muster der Best\u00e4ndigkeit. Zwei Verk\u00e4ufer k\u00f6nnen beide \u201e4,8 Sterne\u201c zeigen, doch der eine hat 900 Bewertungen gleichm\u00e4\u00dfig \u00fcber ein Jahr, der andere 12 aus der letzten Woche. Der erste ist deutlich sicherer.',
        },
        { type: 'h3', text: 'Positive Signale' },
        {
          type: 'list',
          items: [
            'Stetig positive Bewertungen \u00fcber viele Monate, kein pl\u00f6tzlicher Sprung',
            'Ein fokussiertes Sortiment statt \u201ealles f\u00fcr alle\u201c',
            'Schnelle, klare Antworten auf eine Gr\u00f6\u00dfenfrage',
            'Aktuelle QC-Fotos in Bewertungen, die zum Angebot passen',
          ],
        },
        { type: 'h3', text: 'Warnsignale' },
        {
          type: 'list',
          items: [
            'Keine Bewertungshistorie oder ein brandneues Konto',
            'Eine H\u00e4ufung j\u00fcngster Beschwerden \u00fcber Qualit\u00e4t oder falsche Artikel',
            'Preise deutlich unter jedem vergleichbaren Angebot',
            'Langsame, ausweichende oder kopierte Antworten',
          ],
        },
        { type: 'h2', id: 's5', text: 'QC-Fotos wie ein Profi lesen' },
        {
          type: 'p',
          text: 'QC-Fotos \u2014 Qualit\u00e4tskontrolle \u2014 werden im Lager aufgenommen, bevor Ihr Artikel international versandt wird. Das ist Ihre eine saubere Chance, einen Mangel zu erkennen, solange die R\u00fccksendung noch g\u00fcnstig und einfach ist. Diesen Schritt zu \u00fcberspringen ist der h\u00e4ufigste Anf\u00e4ngerfehler \u2014 und der teuerste.',
        },
        {
          type: 'p',
          text: 'Wenn die Fotos ankommen, schauen Sie nicht nur kurz hin und best\u00e4tigen. Zoomen Sie hinein. Bei Schuhen: pr\u00fcfen Sie die N\u00e4hte an der Schuhspitze, die Sohlenverklebung und ob beide Schuhe \u00fcbereinstimmen. Bei Kleidung: achten Sie auf gleichm\u00e4\u00dfige F\u00e4rbung, saubere N\u00e4hte und korrekte Ma\u00dfe. Bei Taschen: pr\u00fcfen Sie die Beschl\u00e4ge \u2014 Rei\u00dfverschl\u00fcsse, Schnallen und Struktur.',
        },
        {
          type: 'proscons',
          pros: [
            'M\u00e4ngel erkennen, bevor Sie internationalen Versand zahlen',
            'Best\u00e4tigen, dass Gr\u00f6\u00dfe und Farbe zum Angebot passen',
            'Vertrauen gewinnen, dass der Verk\u00e4ufer zuverl\u00e4ssig ist',
            'Ein Fotoprotokoll f\u00fcr den Fall einer R\u00fcckerstattung anlegen',
          ],
          cons: [
            'F\u00fcgt eine kurze Wartezeit f\u00fcr die Fotos hinzu',
            'Erfordert echtes Pr\u00fcfen, kein blindes Abnicken',
            'Manche M\u00e4ngel sind allein auf Fotos schwer zu beurteilen',
          ],
        },
        { type: 'h2', id: 's6', text: 'Was Versand wirklich kostet' },
        {
          type: 'p',
          text: 'Versand ist oft das, was neue K\u00e4ufer \u00fcberrascht, weil er separat berechnet wird, nachdem Ihre Artikel im Lager angekommen sind. Der Endpreis h\u00e4ngt von Gewicht, Ma\u00dfen, Ziel und gew\u00e4hlter Linie ab. Als grobe Orientierung sehen die Abw\u00e4gungen so aus.',
        },
        {
          type: 'table',
          headers: ['Methode', 'Typische Dauer', 'Kostenniveau', 'Am besten f\u00fcr'],
          rows: [
            ['Express', '7\u201314 Tage', 'H\u00f6her', 'Zeitkritische oder hochwertige Bestellungen'],
            ['Standard', '15\u201325 Tage', 'Mittel', 'Die meisten Alltagsk\u00e4ufe'],
            ['\u00d6konomie', '25\u201340 Tage', 'Niedriger', 'Schwere Artikel, bei denen der Preis z\u00e4hlt'],
          ],
        },
        {
          type: 'p',
          text: 'Der gr\u00f6\u00dfte Hebel, den Sie steuern, ist die Konsolidierung \u2014 mehrere Artikel in einem Paket zu b\u00fcndeln, um nur einmal Versand zu zahlen. F\u00fcr eine tiefere Aufschl\u00fcsselung Land f\u00fcr Land sehen Sie unseren speziellen Versandkosten-Leitfaden am Ende des Artikels.',
        },
        { type: 'h2', id: 's7', text: 'Vor- und Nachteile dieser Kaufweise' },
        {
          type: 'p',
          text: 'Keine Methode ist perfekt. Ehrlich zu den Abw\u00e4gungen zu sein, macht Sie zu einem ruhigeren, kl\u00fcgeren K\u00e4ufer.',
        },
        {
          type: 'proscons',
          pros: [
            'Zugang zu Produkten, die lokal schwer zu finden sind',
            'Starker Preisvorteil in vielen Kategorien',
            'QC-Fotos geben echte Kontrolle \u00fcber die Qualit\u00e4t',
            'Konsolidierung h\u00e4lt den Versand effizient',
          ],
          cons: [
            'L\u00e4ngere Lieferzeiten als lokales Shopping',
            'Eine Lernkurve bei den ersten Bestellungen',
            'Qualit\u00e4t schwankt, daher ist Verk\u00e4uferpr\u00fcfung entscheidend',
            'Versandkosten k\u00f6nnen Unvorbereitete \u00fcberraschen',
          ],
        },
        { type: 'h2', id: 's8', text: 'Letzte Tipps vor Ihrer ersten Bestellung' },
        {
          type: 'p',
          text: 'Fangen Sie klein an. Ihre erste Bestellung ist eine Lektion, kein Einkaufsrausch \u2014 kaufen Sie einen risikoarmen Artikel, durchlaufen Sie den gesamten Prozess und erkennen Sie, wo Sie z\u00f6gern. Bewahren Sie Screenshots Ihrer Bestellungen und Gespr\u00e4che auf. Nutzen Sie stets die plattformeigene Zahlung und Nachrichten, um gesch\u00fctzt zu bleiben. Tun Sie diese wenigen Dinge, und der Rest wird erstaunlich schnell zur Routine.',
        },
        {
          type: 'quote',
          text: 'Die K\u00e4ufer mit den besten Erfahrungen sind nicht die, die am meisten ausgeben \u2014 sondern die, die an zwei Momenten langsamer werden: bei der Verk\u00e4uferwahl und beim Lesen der QC-Fotos.',
        },
      ],
      faqs: [
        {
          q: 'Muss ich f\u00fcr den Zugang zum Spreadsheet bezahlen?',
          a: 'Das Spreadsheet selbst ist ein Nachschlageindex. Sie zahlen nur, wenn Sie die verlinkten Artikel kaufen, und die Zahlung erfolgt \u00fcber die Plattform, nicht \u00fcber das Spreadsheet.',
        },
        {
          q: 'Ist das AllChinaBuy Spreadsheet offiziell?',
          a: 'Diese Website ist eine unabh\u00e4ngige Ressource und nicht offiziell mit AllChinaBuy verbunden. Wir b\u00fcndeln und erkl\u00e4ren \u00f6ffentlich verf\u00fcgbare Informationen, damit Sie sicherer einkaufen.',
        },
        {
          q: 'Wie lange dauert eine typische Bestellung?',
          a: 'Das h\u00e4ngt von Versandart und Ziel ab, aber die meisten Bestellungen treffen zwischen zwei und f\u00fcnf Wochen ein, wenn Bearbeitung, QC und internationaler Transport eingerechnet sind.',
        },
        {
          q: 'Was ist der wichtigste Schritt f\u00fcr Anf\u00e4nger?',
          a: 'Die QC-Fotos sorgf\u00e4ltig zu lesen, bevor Sie den Versand best\u00e4tigen. Es ist der g\u00fcnstigste und einfachste Moment, ein Problem zu erkennen.',
        },
      ],
    },
  },
};